import {
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  Info,
  Power,
  RefreshCw,
  TrainTrack,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";
import { colocaToast } from "@/shared/utils/toast";

import { OrganizationScopePicker } from "../components/OrganizationScopePicker";
import { DisableRailDialog, type DisableRailIntent } from "../components/rails/DisableRailDialog";
import { RailAvailabilityCards } from "../components/rails/RailAvailabilityCards";
import { RailAvailabilityTable } from "../components/rails/RailAvailabilityTable";
import { currencyLeftInMaintenance } from "../components/rails/railCellDisplay";
import { useOrganizationsCatalog } from "../hooks/useOrganizationsCatalog";
import { useRailMutations } from "../hooks/useRailMutations";
import { useRailSettings } from "../hooks/useRailSettings";
import type { RailAvailabilityCell } from "../types/paymentRails.types";

type RailFilter = "all" | "disabled" | "overrides";

/**
 * `/app/payments-config/rails` — the rail availability kill-switch, for INTERNAL staff.
 *
 * One matrix, two scopes. The GLOBAL view edits the switch everyone inherits; picking an organization shows
 * what THAT client effectively gets and writes overrides for it — most-specific-wins, so an org override
 * beats the global switch in both senses. Enforcement is the backend's: a disabled rail refuses creation with
 * `rail_disabled` while in-flight operations and their settlement webhooks are untouched.
 *
 * Freshness: no polling — settings change by human action only. Every mutation also drops the shared
 * corridors memo, so the payins page, the wallet cards and the dispersion wizard reflect the flip live.
 */
const RailAvailabilityPage = () => {
  const { t } = useTranslations("paymentRails");
  const { t: tGlobal } = useTranslations("global");
  const isMobile = useIsMobile();

  useAppShellBreadcrumb(
    useMemo(
      () => [
        { label: tGlobal("sidebar_section_gestion") },
        { label: tGlobal("sidebar_menu_payment_rails"), to: "/app/payments-config/rails" },
      ],
      [tGlobal],
    ),
  );

  const [scopeOrgId, setScopeOrgId] = useState<string | null>(null);
  const [filter, setFilter] = useState<RailFilter>("all");
  const [disableIntent, setDisableIntent] = useState<DisableRailIntent | null>(null);
  const [clearTarget, setClearTarget] = useState<RailAvailabilityCell | null>(null);

  const state = useRailSettings(scopeOrgId);
  // The catalog is the page's name book — it resolves the organization behind every override row — so it must
  // NOT narrow to whatever the picker is searching for. The picker runs its own server-side search inside
  // `OrganizationScopePicker`.
  const orgs = useOrganizationsCatalog();
  // One instance serves the quick-enable switches and the clear-override dialog; the disable dialog owns its
  // own (its error renders inside the form).
  const mutations = useRailMutations();

  const orgNameById = useMemo(
    () => new Map(orgs.organizations.map((org) => [org.id, org.name])),
    [orgs.organizations],
  );

  const scopeLabels = useMemo(
    () => ({
      label: t("scope_label"),
      placeholder: t("scope_placeholder"),
      loading: t("scope_loading"),
      empty: t("scope_empty"),
      searching: t("scope_searching"),
      searchError: t("scope_search_error"),
      catalogError: t("scope_catalog_error"),
      catalogTruncated: t("scope_catalog_truncated"),
      globalOption: t("scope_global_option"),
      globalHint: t("scope_global_hint"),
      orgHint: t("scope_org_hint"),
    }),
    [t],
  );

  const overrideCount = useMemo(
    () =>
      scopeOrgId
        ? state.cells.filter((cell) => cell.organization !== null).length
        : state.kpis.cellsWithOverrides,
    [scopeOrgId, state.cells, state.kpis.cellsWithOverrides],
  );

  const visibleCells = useMemo(() => {
    if (filter === "disabled") return state.cells.filter((cell) => !cell.effective_enabled);
    if (filter === "overrides") {
      return scopeOrgId
        ? state.cells.filter((cell) => cell.organization !== null)
        : state.cells.filter((cell) => cell.org_overrides.length > 0);
    }
    return state.cells;
  }, [state.cells, filter, scopeOrgId]);

  const payinCells = useMemo(() => visibleCells.filter((cell) => cell.direction === "PAYIN"), [visibleCells]);
  const payoutCells = useMemo(
    () => visibleCells.filter((cell) => cell.direction === "PAYOUT"),
    [visibleCells],
  );

  // Shortcut from an override row straight into that organization's scope, where the override is managed.
  const handleViewOrg = useCallback((organizationId: string) => {
    setScopeOrgId(organizationId);
    setFilter("all");
  }, []);

  // The switch's two directions carry different weight: enabling is safe and flips directly (one click, the
  // code, toast); putting a rail in maintenance opens the dialog because the backend refuses it without a reason.
  const handleToggle = useCallback(
    async (cell: RailAvailabilityCell, nextEnabled: boolean) => {
      if (!nextEnabled) {
        setDisableIntent({
          cell,
          organizationId: scopeOrgId,
          organizationName: scopeOrgId ? orgNameById.get(scopeOrgId) : undefined,
          currencyInMaintenance: currencyLeftInMaintenance(cell, state.cells),
        });
        return;
      }
      const outcome = await mutations.setRailState(cell.rail, {
        direction: cell.direction,
        organization_id: scopeOrgId,
        enabled: true,
      });
      if (!outcome) return;
      if (!outcome.ok) {
        colocaToast.error(outcome.message);
        return;
      }
      colocaToast.success(t("toast_enabled", { rail: formatRail(cell.rail) }));
      state.refresh();
    },
    [scopeOrgId, orgNameById, mutations, state, t],
  );

  const handleDisabled = useCallback(
    (railLabel: string) => {
      setDisableIntent(null);
      colocaToast.success(t("toast_disabled", { rail: railLabel }));
      state.refresh();
    },
    [state, t],
  );

  const openClearOverride = useCallback(
    (cell: RailAvailabilityCell) => {
      // A message from an earlier failed mutation must not greet this dialog.
      mutations.clearError();
      setClearTarget(cell);
    },
    [mutations],
  );

  const handleClearConfirm = useCallback(async () => {
    if (!clearTarget?.organization || !scopeOrgId) return;
    const outcome = await mutations.clearRailScope(clearTarget.rail, {
      // The exact scope of the live row — a both-directions row clears both cells at once.
      direction: clearTarget.organization.direction,
      organization_id: scopeOrgId,
    });
    if (outcome?.ok) {
      setClearTarget(null);
      colocaToast.success(t("toast_override_cleared", { rail: formatRail(clearTarget.rail) }));
      state.refresh();
    }
  }, [clearTarget, scopeOrgId, mutations, state, t]);

  if (!state.hasAccess) return <NoAccessView />;

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <ColocaButton
            variant="default"
            size="sm"
            icon={RefreshCw}
            aria-label={t("refresh_aria")}
            className={cn(state.isRefreshing && "[&_svg]:animate-spin")}
            onClick={state.refresh}
          >
            {t("refresh_button")}
          </ColocaButton>
        }
      />

      <OrganizationScopePicker
        id="rail-availability-scope"
        className="max-w-md"
        value={scopeOrgId}
        onValueChange={(organizationId) => {
          setScopeOrgId(organizationId);
          setFilter("all");
        }}
        catalog={orgs}
        labels={scopeLabels}
      />

      {state.hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          heading={t("error_load")}
          text={t("error_load_detail")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={state.refresh}>
              {t("error_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <>
          {state.isLoading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[0, 1, 2].map((index) => (
                <ColocaSkeleton key={index} className="h-[5.5rem] rounded-xl" />
              ))}
            </div>
          ) : (
            // The strip IS the filter: each tile narrows the table to the cells it counts.
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <ColocaKpiCard
                icon={TrainTrack}
                accent="primary"
                label={t("kpi_all")}
                value={String(state.kpis.totalCells)}
                selected={filter === "all"}
                onSelect={() => setFilter("all")}
              />
              <ColocaKpiCard
                icon={Power}
                accent={state.kpis.disabledCells > 0 ? "danger" : "neutral"}
                label={t("kpi_disabled")}
                value={String(state.kpis.disabledCells)}
                selected={filter === "disabled"}
                onSelect={() => setFilter("disabled")}
              />
              <ColocaKpiCard
                icon={Building2}
                accent={overrideCount > 0 ? "info" : "neutral"}
                label={t(scopeOrgId ? "kpi_own_overrides" : "kpi_overrides")}
                value={String(overrideCount)}
                selected={filter === "overrides"}
                onSelect={() => setFilter("overrides")}
              />
            </div>
          )}

          {!state.isLoading && visibleCells.length === 0 ? (
            <ColocaEmpty icon={TrainTrack} title={t("empty_title")} description={t("empty_description")} />
          ) : (
            [
              {
                key: "payins",
                icon: ArrowDownToLine,
                titleKey: "section_payins" as const,
                cells: payinCells,
                skeletonRows: 7,
              },
              {
                key: "payouts",
                icon: ArrowUpFromLine,
                titleKey: "section_payouts" as const,
                cells: payoutCells,
                skeletonRows: 5,
              },
            ].map(({ key, icon: SectionIcon, titleKey, cells, skeletonRows }) => {
              // A filtered-out section disappears entirely — one global empty state is enough.
              if (!state.isLoading && cells.length === 0) return null;
              return (
                <section key={key} className="space-y-3">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    <SectionIcon className="size-4 text-muted-foreground" />
                    {t(titleKey)}
                  </h2>
                  {isMobile ? (
                    <RailAvailabilityCards
                      cells={cells}
                      isLoading={state.isLoading}
                      scopeOrgId={scopeOrgId}
                      orgNameById={orgNameById}
                      isMutating={mutations.isSubmitting || state.isRefreshing}
                      onToggle={(cell, nextEnabled) => void handleToggle(cell, nextEnabled)}
                      onClearOverride={openClearOverride}
                      onViewOrg={handleViewOrg}
                    />
                  ) : (
                    <RailAvailabilityTable
                      cells={cells}
                      isLoading={state.isLoading}
                      skeletonRows={skeletonRows}
                      scopeOrgId={scopeOrgId}
                      orgNameById={orgNameById}
                      isMutating={mutations.isSubmitting || state.isRefreshing}
                      onToggle={(cell, nextEnabled) => void handleToggle(cell, nextEnabled)}
                      onClearOverride={openClearOverride}
                      onViewOrg={handleViewOrg}
                    />
                  )}
                </section>
              );
            })
          )}

          {/* Why this matters — the switch gates creation only; money in flight keeps settling. */}
          <ColocaAlert variant="secondary" icon={Info} text={t("why_banner")} />
        </>
      )}

      {disableIntent && (
        <DisableRailDialog
          intent={disableIntent}
          open
          onClose={() => setDisableIntent(null)}
          onDisabled={handleDisabled}
          orgNameById={orgNameById}
        />
      )}

      {clearTarget && (
        <ColocaAlertDialog
          open
          onOpenChange={(next) => {
            if (!next && !mutations.isSubmitting) setClearTarget(null);
          }}
          title={t("clear_title")}
          description={t("clear_description")}
          confirmLabel={t("clear_confirm")}
          confirmDisabled={mutations.isSubmitting}
          cancelLabel={t("clear_cancel")}
          onConfirm={() => void handleClearConfirm()}
          onCancel={() => setClearTarget(null)}
        >
          <div className="space-y-3">
            {clearTarget.organization?.direction === null && (
              <ColocaAlert variant="warning" icon={AlertCircle} text={t("clear_both_directions")} />
            )}
            {mutations.error && <ColocaAlert variant="destructive" text={mutations.error} />}
          </div>
        </ColocaAlertDialog>
      )}

      <TwoFactorActionModals
        controller={mutations.twoFactor}
        requiredDescription={t("two_factor_required")}
        challengeDescription={t("two_factor_description")}
      />
    </div>
  );
};

export default RailAvailabilityPage;
